import React from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { useTranslation } from '../../hooks/useTranslation';
import PageHeader from '../../components/base/PageHeader';
import LoadingState from '../../components/base/LoadingState';
import TariffCard from '../../components/payment/TariffCard';
import {
    getTariffById,
    getTariffsMoreExpensiveThan,
    getNewFeatures,
    Tariff,
    TariffFeature,
} from '../../config/tariffs';
import { useTariff } from '../../hooks/useTariff';

const TariffPage: React.FC = () => {
    const { t } = useTranslation();
    const { tariff, isLoading: isTariffLoading, tariffType } = useTariff();

    if (isTariffLoading) {
        return <LoadingState message={t.pages.support.loading} />;
    }

    const moreExpensiveTariffs = getTariffsMoreExpensiveThan(tariff.id);

    // Находим предыдущий тариф для каждого более дорогого тарифа
    const getPreviousTariff = (tariffId: number): { name: string; tariff: Tariff } | undefined => {
        const allTariffs = [1116, 1117, 1118];
        const currentIndex = allTariffs.indexOf(tariff.id);
        const tariffIndex = allTariffs.indexOf(tariffId);

        if (tariffIndex > 0 && tariffIndex > currentIndex) {
            const previousTariffId = allTariffs[tariffIndex - 1];
            const previousTariff = getTariffById(previousTariffId);
            if (previousTariff) {
                return { name: previousTariff.name, tariff: previousTariff };
            }
        }
        return undefined;
    };

    // Подготавливаем данные для тарифов
    const tariffCards: Array<{
        tariff: Tariff;
        isCurrent: boolean;
        previousTariffName?: string;
        features?: TariffFeature[];
    }> = [];

    // Добавляем текущий тариф
    if (tariff && tariffType !== 'not_set') {
        tariffCards.push({
            tariff: tariff,
            isCurrent: true,
            previousTariffName: undefined,
            features: undefined,
        });
    }

    // Добавляем более дорогие тарифы
    moreExpensiveTariffs.forEach((tariff) => {
        const previousTariffInfo = getPreviousTariff(tariff.id);
        const newFeatures = getNewFeatures(tariff, previousTariffInfo?.tariff);
        tariffCards.push({
            tariff,
            isCurrent: false,
            previousTariffName: previousTariffInfo?.name,
            features: newFeatures,
        });
    });

    return (
        <Box h="calc(100vh - 60px)" overflow="auto" bg="gray.100" pt={2} pb={{ base: 20, lg: 6 }}>
            <PageHeader title={t.pages.payment.tariff} backPath="/payment" />
            <Box maxW={{ base: '4xl', lg: 'full' }} mx="auto" px={4} pt={4}>
                <Flex direction={{ base: 'column', lg: 'row' }} gap={6} align="stretch">
                    {tariffCards.map((card) => (
                        <TariffCard
                            key={card.tariff.id}
                            tariff={card.tariff}
                            isCurrent={card.isCurrent}
                            previousTariffName={card.previousTariffName}
                            features={card.features}
                        />
                    ))}
                </Flex>
            </Box>
        </Box>
    );
};

export default TariffPage;
